import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, MapPin, Award, Check } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../../lib/supabase';
import { QUERY_KEYS } from '../../../lib/queryKeys';
import { whatsappMessages } from '../../../data/whatsappMessages';
import WhatsAppIcon from '../../../components/ui/WhatsAppIcon';
import { getOptimizedUrl } from '../../../utils/imageUtils';
import AnimatedCounter from '../../../components/ui/AnimatedCounter/AnimatedCounter';

/*
 * Hero — sección de referencia del rediseño v2 (ago-2026).
 * - Motivo de firma A1→A2→B1→B2→C1: vive EXCLUSIVAMENTE en el Hero.
 * - El rojo (aeb-red) se usa solo en el CTA de "Prueba de ubicación" (uno de los 3
 *   únicos rojos de la página, junto al bloque dedicado y la sticky bar).
 * - Se conserva el carrusel de campus alimentado por Supabase (tabla carousel).
 */

const WA_HREF = `https://wa.me/50687704499?text=${encodeURIComponent(whatsappMessages.HERO_WHATSAPP)}`;

const isSafeUrl = (url) => {
  if (!url) return false;
  try {
    const u = new URL(url);
    return ['https:', 'http:'].includes(u.protocol);
  } catch { return false; }
};

// Motivo de firma — recorrido de niveles MCER.
const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1'];

const TRUST_STATS = [
  { prefix: '+', end: 25, suffix: '.000', label: 'Egresados' },
  { prefix: '+', end: 20, label: 'Años' },
  { prefix: '+', end: 40, label: 'Sedes' },
  { fixed: 'B2-C1', label: 'Nivel garantizado' },
];

const MARQUEE_ITEMS = [
  'Certificación Oxford & TOEIC',
  '100% conversacional',
  'Profesores certificados C1',
  '+40 sedes o en vivo online',
  'Nivel B2-C1 garantizado',
];

const fetchHeroSlides = async () => {
  const { data, error } = await supabase
    .from('carousel')
    .select('id, imagen_url, titulo, subtitulo, orden')
    .eq('activo', true)
    .order('orden', { ascending: true })
    .limit(5);
  if (error) throw error;
  return (data || []).filter((it) => isSafeUrl(it.imagen_url)).slice(0, 5);
};

export default function Hero() {
  const reduce = useReducedMotion();
  const { data: slides = [] } = useQuery({
    queryKey: QUERY_KEYS.carousel,
    queryFn: fetchHeroSlides,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const [idx, setIdx] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (slides.length < 2) return undefined;
    intervalRef.current = setInterval(() => {
      setIdx((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(intervalRef.current);
  }, [slides.length]);

  return (
    <section
      id="inicio"
      className="relative min-h-[100dvh] overflow-hidden bg-aeb-navy font-plex"
    >
      {/* ── Fondo: carrusel de campus + capas navy ── */}
      <div className="absolute inset-0">
        {/* Fallback estático (LCP) */}
        <picture>
          <source srcSet="/hero-fallback-mobile.webp" media="(max-width: 640px)" type="image/webp" />
          <source srcSet="/hero-fallback.webp" media="(min-width: 641px)" type="image/webp" />
          <img
            src="/hero-fallback.jpg"
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            fetchpriority="high"
            decoding="sync"
          />
        </picture>
        {slides.map((s, i) => (
          <img
            key={s.id || i}
            src={getOptimizedUrl(s.imagen_url, 1400, 85)}
            alt={s.titulo || ''}
            className="absolute inset-0 h-full w-full object-cover transition-opacity duration-[1200ms] ease-out"
            style={{ opacity: i === idx ? 1 : 0 }}
            loading={i === 0 ? 'eager' : 'lazy'}
            decoding="async"
          />
        ))}
        {/* Degradado navy — legibilidad del texto */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(105deg, #060A26 0%, rgba(11,20,64,0.94) 42%, rgba(11,20,64,0.62) 72%, rgba(11,20,64,0.35) 100%)',
          }}
        />
        {/* Halo gold sutil */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-40 top-10 h-[460px] w-[460px] rounded-full opacity-[0.12] blur-3xl"
          style={{ background: 'radial-gradient(circle, #C9962B 0%, transparent 70%)' }}
        />
        {/* Grid tenue */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(#FBFBFA 1px, transparent 1px), linear-gradient(90deg, #FBFBFA 1px, transparent 1px)',
            backgroundSize: '72px 72px',
          }}
        />
      </div>

      {/* ── Contenido ── */}
      <div className="relative mx-auto flex min-h-[100dvh] max-w-[1200px] flex-col justify-start px-6 pb-24 pt-[calc(var(--nav-height,90px)+var(--banner-height,0px)+1.75rem)] lg:justify-center lg:pb-32">
        <motion.div
          initial={reduce ? false : 'hidden'}
          animate="show"
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } } }}
          className="max-w-[760px]"
        >
          {/* Eyebrow — posiciona programa + público de inmediato */}
          <motion.span
            variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } }}
            className="inline-flex items-center gap-2 rounded-full border border-aeb-gold/30 bg-aeb-gold/10 px-4 py-1.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-aeb-gold-soft"
          >
            <Award size={14} strokeWidth={2} />
            Inglés conversacional para profesionales
          </motion.span>

          {/* Headline */}
          <motion.h1
            variants={{ hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0 } }}
            className="mt-6 font-instrument text-[2.6rem] font-bold leading-[1.03] tracking-tight text-aeb-paper sm:text-6xl lg:text-[4.2rem]"
          >
            Domina el inglés
            <br />
            en{' '}
            <span className="relative whitespace-nowrap text-aeb-gold">
              9 meses
              <svg
                className="absolute -bottom-2 left-0 w-full"
                height="10"
                viewBox="0 0 200 10"
                fill="none"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path d="M2 7 Q100 1 198 6" stroke="#C9962B" strokeWidth="3" strokeLinecap="round" opacity="0.75" />
              </svg>
            </span>
          </motion.h1>

          {/* Subheadline — conecta resultado + contexto real de uso */}
          <motion.p
            variants={{ hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0 } }}
            className="mt-5 max-w-[54ch] text-[17px] leading-relaxed text-aeb-muted-light md:mt-7 md:text-lg"
          >
            Desarrollá la confianza para hablar inglés en{' '}
            <span className="font-semibold text-aeb-paper">reuniones, entrevistas y con clientes</span>,
            y convertí ese nivel en mejores oportunidades profesionales.
          </motion.p>

          {/* Motivo de firma — recorrido A1 → C1 (solo en el Hero) */}
          <motion.div
            variants={{ hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0 } }}
            className="mt-7 max-w-[440px] md:mt-9"
          >
            <div className="mb-2.5 flex items-center justify-between text-[11px] font-medium uppercase tracking-[0.16em] text-aeb-muted-dark">
              <span>Tu punto de partida</span>
              <span className="text-aeb-gold-soft">Tu meta</span>
            </div>
            <div className="relative flex items-center justify-between">
              {/* Línea base */}
              <div className="absolute left-0 right-0 top-1/2 h-[2px] -translate-y-1/2 bg-aeb-paper/12" />
              {/* Línea de progreso animada */}
              <motion.div
                className="absolute left-0 top-1/2 h-[2px] -translate-y-1/2 bg-gradient-to-r from-aeb-gold/60 to-aeb-gold"
                initial={reduce ? { width: '100%' } : { width: '0%' }}
                animate={{ width: '100%' }}
                transition={{ duration: 1.3, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
              />
              {LEVELS.map((lvl, i) => {
                const isGoal = i === LEVELS.length - 1;
                return (
                  <motion.span
                    key={lvl}
                    initial={reduce ? false : { scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.35, delay: 0.55 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                    className={`relative z-10 flex h-9 w-9 items-center justify-center rounded-full text-[12px] font-bold ring-1 ${
                      isGoal
                        ? 'bg-aeb-gold text-aeb-navy-deep ring-aeb-gold shadow-[0_0_20px_-2px_rgba(201,150,43,0.7)]'
                        : 'bg-aeb-navy-deep text-aeb-muted-light ring-aeb-paper/15'
                    }`}
                  >
                    {lvl}
                  </motion.span>
                );
              })}
            </div>
          </motion.div>

          {/* CTAs — embudo único: WhatsApp directo como acción PRIMARIA */}
          <motion.div
            variants={{ hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0 } }}
            className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center md:mt-10"
          >
            {/* CTA PRIMARIO — WhatsApp directo (menor fricción; rojo = acción de conversión) */}
            <a
              href={WA_HREF}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-aeb-red px-8 py-4 text-[16.5px] font-semibold text-white shadow-[0_14px_38px_-10px_rgba(209,53,43,0.7)] transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0"
            >
              <WhatsAppIcon size={19} /> Hablar con un asesor
              <ArrowRight size={18} strokeWidth={2} className="transition-transform duration-200 group-hover:translate-x-1" />
            </a>
            {/* CTA SECUNDARIO — prueba de nivel (mismo embudo, subordinado en peso visual) */}
            <Link
              to="/prueba-ubicacion"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-aeb-paper/25 px-7 py-3.5 text-[15px] font-medium text-aeb-paper transition-colors duration-200 hover:border-aeb-paper/40 hover:bg-aeb-paper/10"
            >
              Hacé tu prueba de nivel gratis
            </Link>
          </motion.div>

          {/* 3 checks de valor inmediato — cualitativos (no repiten los stats numéricos) */}
          <motion.ul
            variants={{ hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0 } }}
            className="mt-5 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-x-6 sm:gap-y-2"
          >
            {['Hablás desde la primera clase', 'Presencial o 100% en vivo online', 'Certificación internacional opcional'].map((c) => (
              <li key={c} className="inline-flex items-center gap-2 text-[14px] font-medium text-aeb-paper">
                <Check size={15} strokeWidth={2.6} className="text-aeb-gold-soft" />
                {c}
              </li>
            ))}
          </motion.ul>

          {/* Refuerzo de embudo único — baja fricción, misma acción de conversión */}
          <motion.p
            variants={{ hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0 } }}
            className="mt-3.5 flex items-center gap-2 text-[13px] text-aeb-muted-dark"
          >
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-aeb-gold-soft" />
            Te respondemos en minutos · Sin compromiso
          </motion.p>

          {/* Trust stats */}
          <motion.div
            variants={{ hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0 } }}
            className="mt-12 flex flex-wrap gap-x-9 gap-y-4 border-t border-aeb-paper/10 pt-7"
          >
            {TRUST_STATS.map((s) => (
              <div key={s.label} className="flex flex-col">
                <span className="font-instrument text-2xl font-bold text-aeb-paper">
                  {s.fixed ? s.fixed : <AnimatedCounter end={s.end} prefix={s.prefix} suffix={s.suffix || ''} />}
                </span>
                <span className="text-[13px] text-aeb-muted-dark">{s.label}</span>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Caption del slide activo (abajo-derecha) */}
      {slides[idx]?.titulo && (
        <div className="absolute bottom-24 right-6 z-10 hidden max-w-[240px] items-center gap-2 rounded-full border border-aeb-paper/10 bg-aeb-navy-deep/70 px-4 py-2 text-[12.5px] text-aeb-muted-light backdrop-blur lg:flex">
          <MapPin size={13} className="shrink-0 text-aeb-gold-soft" />
          <span className="truncate">{slides[idx].titulo}</span>
        </div>
      )}

      {/* Marquee inferior */}
      <div className="absolute bottom-0 left-0 right-0 overflow-hidden border-t border-aeb-paper/10 bg-aeb-navy-deep/80 py-3 backdrop-blur">
        <motion.div
          className="flex w-max gap-10 whitespace-nowrap px-5 text-[13px] font-medium text-aeb-muted-light"
          animate={reduce ? undefined : { x: ['0%', '-50%'] }}
          transition={{ duration: 22, repeat: Infinity, ease: 'linear' }}
        >
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((t, i) => (
            <span key={i} className="inline-flex items-center gap-3">
              {t}
              <span className="inline-block h-1 w-1 rounded-full bg-aeb-gold/60" />
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
